import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MetaApp } from '../types/meta.types';
import { Shield, ExternalLink, Check } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const REQUIRED_SCOPE_LABELS: Record<string, string> = {
    pages_show_list: 'Listar páginas',
    pages_messaging: 'Mensagens da página',
    pages_manage_metadata: 'Webhooks da página',
};

const OPTIONAL_SCOPE_LABELS: Record<string, string> = {
    instagram: 'Instagram',
    instagram_basic: 'Perfil do Instagram',
    instagram_manage_messages: 'Mensagens diretas (Instagram)',
    email: 'E-mail do perfil',
    business_management: 'Gerenciador de Negócios',
    pages_read_engagement: 'Ler engajamento',
    pages_read_user_content: 'Ler conteúdo de usuários',
    pages_manage_engagement: 'Responder a comentários',
    pages_utility_messaging: 'Utility Messaging',
};

const REQUIRED_SCOPES = ['pages_show_list', 'pages_messaging', 'pages_manage_metadata'] as const;

interface ConnectAccountDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    apps: MetaApp[];
    selectedAppId: number | null;
    onAppSelect: (appId: number | null) => void;
    onConnect: () => void;
    isLoading: boolean;
    selectedApp?: MetaApp;
}

export default function ConnectAccountDialog({
                                                 open,
                                                 onOpenChange,
                                                 apps,
                                                 selectedAppId,
                                                 onAppSelect,
                                                 onConnect,
                                                 isLoading,
                                                 selectedApp,
                                             }: ConnectAccountDialogProps) {
    const activeApps = apps.filter(app => app.is_active);

    const optionalScopes = selectedApp?.oauth_scopes ?? [];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-[#0f172a]">
                        Conectar Conta Meta
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        Selecione qual aplicação será usada para autenticação.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Lista de Apps */}
                    <div className="space-y-2">
                        <Label className="text-gray-700 font-medium">
                            Aplicação
                        </Label>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                            {activeApps.map((app) => (
                                <div
                                    key={app.id}
                                    onClick={() => onAppSelect(app.id)}
                                    className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                        selectedAppId === app.id
                                            ? 'border-[#0f172a] bg-[#0f172a]/5'
                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        {app.is_default && <Shield className="h-4 w-4 text-blue-600" />}
                                        <div>
                                            <span className="font-medium text-sm">{app.app_name}</span>
                                            <span className="text-xs text-gray-500 font-mono ml-2">
                                                ({app.app_id})
                                            </span>
                                            {app.is_default && (
                                                <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                                    Padrão
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {selectedAppId === app.id && (
                                        <Check className="h-5 w-5 text-[#0f172a]" />
                                    )}
                                </div>
                            ))}
                            {activeApps.length === 0 && (
                                <div className="p-4 text-center text-sm text-gray-500 border border-dashed border-gray-300 rounded-lg">
                                    Nenhuma app disponível. Crie uma app primeiro.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Informações do Processo */}
                    <Alert>
                        <ExternalLink className="h-4 w-4" />
                        <AlertDescription className="text-sm text-gray-700">
                            <strong className="font-semibold">O que acontecerá:</strong>
                            <ol className="mt-2 ml-4 list-decimal space-y-1">
                                <li>Você será redirecionado para o Facebook</li>
                                <li>Faça login e autorize as permissões solicitadas</li>
                                <li>Suas páginas serão sincronizadas automaticamente</li>
                                <li>Você poderá usar as páginas para enviar broadcasts</li>
                            </ol>
                        </AlertDescription>
                    </Alert>

                    {/* Permissões que serão solicitadas */}
                    <div className="rounded-lg border border-gray-200 p-4 space-y-3">
                        <h4 className="font-semibold text-sm text-gray-900">
                            Permissões Solicitadas:
                        </h4>

                        {/* Obrigatórias */}
                        <div className="space-y-1">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Obrigatório</p>
                            <ul className="space-y-1">
                                {REQUIRED_SCOPES.map((scope) => (
                                    <li key={scope} className="flex items-center gap-2 text-sm text-gray-700">
                                        <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#0f172a]" />
                                        {REQUIRED_SCOPE_LABELS[scope]}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Opcionais do app selecionado */}
                        {optionalScopes.length > 0 && (
                            <div className="space-y-1 pt-1 border-t border-gray-100">
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Opcional</p>
                                <ul className="space-y-1">
                                    {optionalScopes.map((scope) => {
                                        const label = OPTIONAL_SCOPE_LABELS[scope] ?? scope;
                                        return (
                                            <li key={scope} className="flex items-center gap-2 text-sm text-gray-600">
                                                <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                                                {label}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isLoading}
                        className="border-gray-300"
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={onConnect}
                        disabled={isLoading || !selectedAppId}
                        className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                    >
                        {isLoading ? (
                            <>
                                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                Conectando...
                            </>
                        ) : (
                            <>
                                <ExternalLink className="mr-2 h-4 w-4" />
                                Conectar com Facebook
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
